import React, { useState } from 'react';
import { ProficiencyLevel, CreateConversationRequest } from '../../types/chat';
import { createConversation } from './api/conversationAPI';
import { useLanguage } from '../../context/LanguageContext';

interface NewConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConversationCreated: (conversationId: number) => void;
}

const PROFICIENCY_LEVELS: { value: ProficiencyLevel; label: string; description: string }[] = [
  { value: 'A1', label: 'A1 - Beginner', description: 'Can understand and use familiar everyday expressions' },
  { value: 'A2', label: 'A2 - Elementary', description: 'Can communicate in simple and routine tasks' },
  { value: 'B1', label: 'B1 - Intermediate', description: 'Can deal with most situations while traveling' },
  { value: 'B2', label: 'B2 - Upper Intermediate', description: 'Can interact with fluency and spontaneity' },
  { value: 'C1', label: 'C1 - Advanced', description: 'Can express ideas fluently and spontaneously' },
  { value: 'C2', label: 'C2 - Proficient', description: 'Can express themselves spontaneously and precisely' },
];

const NewConversationModal: React.FC<NewConversationModalProps> = ({
  isOpen,
  onClose,
  onConversationCreated,
}) => {
  const { selectedLanguage } = useLanguage();
  const [title, setTitle] = useState('');
  const [proficiencyLevel, setProficiencyLevel] = useState<ProficiencyLevel>('A1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedLanguage) {
      setError('Please select a language from the top menu');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const conversationData: CreateConversationRequest = {
        title: title.trim() || undefined,
        conversation_language: selectedLanguage.iso639_tag,
        proficiency_level: proficiencyLevel,
      };

      const newConversation = await createConversation(conversationData);
      onConversationCreated(newConversation.conversation_id);
      
      // Reset form
      setTitle('');
      setProficiencyLevel('A1');
      onClose();
    } catch (err: any) {
      console.error('Error creating conversation:', err);
      setError(err.message || 'Failed to create conversation');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box">
        <h3 className="font-bold text-lg mb-4">Start New Conversation</h3>
        
        {error && (
          <div className="alert alert-error mb-4">
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div className="form-control mb-4">
            <label className="label">
              <span className="label-text">Title (optional)</span>
            </label>
            <input
              type="text"
              placeholder="e.g., Daily Practice, Travel Prep..."
              className="input input-bordered w-full"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
            />
          </div>

          {/* Language Display */}
          <div className="form-control mb-4">
            <label className="label">
              <span className="label-text">Language</span>
            </label>
            <div className="alert alert-info">
              <span>
                {selectedLanguage ? selectedLanguage.language_name : 'No language selected'}
              </span>
            </div>
            {!selectedLanguage && (
              <label className="label">
                <span className="label-text-alt text-error">
                  Please select a language from the top menu before creating a conversation
                </span>
              </label>
            )}
          </div>

          {/* Proficiency Level */}
          <div className="form-control mb-6">
            <label className="label">
              <span className="label-text">Your Proficiency Level *</span>
            </label>
            <select
              className="select select-bordered w-full"
              value={proficiencyLevel}
              onChange={(e) => setProficiencyLevel(e.target.value as ProficiencyLevel)}
              required
            >
              {PROFICIENCY_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
            <label className="label">
              <span className="label-text-alt text-xs">
                {PROFICIENCY_LEVELS.find(l => l.value === proficiencyLevel)?.description}
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="modal-action">
            <button
              type="button"
              className="btn"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`btn btn-primary ${loading ? 'loading' : ''}`}
              disabled={loading || !selectedLanguage}
            >
              {loading ? 'Creating...' : 'Create Conversation'}
            </button>
          </div>
        </form>
      </div>
      <div className="modal-backdrop" onClick={handleClose}></div>
    </div>
  );
};

export default NewConversationModal;
